/**
 * `dropdown` → **Edit Options** (spec M1-B5 §6.2, M2 §5.8).
 *
 * `dropdown` settings are an **array** (`labels: [{id, name}]`) rather than status's id→text
 * map, and the id is again immutable: a new option takes `max(id) + 1`, a removed one is
 * dropped from `labels` and remembered in `deactivated_labels` only when the caller
 * deactivates rather than deletes. `limit_select` + `label_limit_count` are
 * `create_dropdown_column`'s own keys `[09§A.2]` and are round-tripped verbatim.
 */
import type { DropdownSettings } from '@elizmonday/shared';
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';

function nextOptionId(settings: DropdownSettings): number {
  const ids = settings.labels.map((label) => label.id);
  return ids.length === 0 ? 1 : Math.max(...ids) + 1;
}

export function DropdownSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<DropdownSettings>) {
  // Round 10i: `labels` is the one required key, and `column.settings` is `null` on a column
  // monday never configured and `{}` on one this product has only just created.
  const [draft, setDraft] = useState<DropdownSettings>(() => {
    const source = (settings ?? {}) as Partial<DropdownSettings>;
    return { ...source, labels: source.labels ?? [] };
  });
  const patch = (next: Partial<DropdownSettings>) => setDraft((current) => ({ ...current, ...next }));

  return (
    <form
      className="ez-column-settings__form"
      data-testid="dropdown-settings"
      onSubmit={(event) => {
        event.preventDefault();
        onCommit(draft);
      }}
    >
      <h2 className="ez-column-settings__title">Edit Options</h2>

      <ul className="ez-label-editor">
        {draft.labels.map((label, index) => (
          <li key={label.id} className="ez-label-editor__row" data-testid={`option-row-${String(label.id)}`}>
            <input
              className="ez-label-editor__text"
              aria-label={`Option ${String(index + 1)}`}
              value={label.name}
              onChange={(event) =>
                patch({
                  labels: draft.labels.map((entry) =>
                    entry.id === label.id ? { ...entry, name: event.target.value } : entry,
                  ),
                })
              }
            />
            <button
              type="button"
              className="ez-label-editor__icon"
              aria-label={`Delete ${label.name}`}
              data-testid={`option-delete-${String(label.id)}`}
              onClick={() => patch({ labels: draft.labels.filter((entry) => entry.id !== label.id) })}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="ez-column-settings__add"
        data-testid="option-add"
        onClick={() =>
          patch({ labels: [...draft.labels, { id: nextOptionId(draft), name: 'New option' }] })
        }
      >
        + New option
      </button>

      <label className="ez-column-settings__field">
        <input
          type="checkbox"
          checked={draft.limit_select ?? false}
          data-testid="dropdown-limit"
          /*
           * Turning the cap **on** writes the count as well. The number field renders
           * `label_limit_count ?? 1`, so leaving it at its default fires no change and the column
           * was saved with `limit_select: true` and no count at all — a cap of nothing, which the
           * picker cannot enforce and `/v2` returns as half a setting (round 10i, C2 `S2b`).
           */
          onChange={(event) =>
            patch(
              event.target.checked
                ? { limit_select: true, label_limit_count: draft.label_limit_count ?? 1 }
                : { limit_select: false },
            )
          }
        />
        Limit how many options one cell may hold
      </label>
      {draft.limit_select ? (
        <label className="ez-column-settings__field">
          Maximum
          <input
            type="number"
            min={1}
            max={draft.labels.length || 1}
            value={draft.label_limit_count ?? 1}
            data-testid="dropdown-limit-count"
            onChange={(event) => patch({ label_limit_count: Number(event.target.value) })}
          />
        </label>
      ) : null}

      <footer className="ez-column-settings__footer">
        <button type="button" className="ez-column-settings__cancel" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ez-column-settings__save" data-testid="settings-save">
          Apply
        </button>
      </footer>
    </form>
  );
}
